import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslations } from "next-intl";
import { InviteRedeemPanel } from "@/components/invite-redeem-panel";
import { rememberPendingInvite } from "@/lib/pending-invite";
import { broadcastSignOut, clearSessionActivity } from "@/lib/session-activity";
import { logoutSession } from "@/services/api/auth";
import { useAuthStore } from "@/stores/authStore";

const PRIMARY =
  "mt-6 inline-block rounded-md bg-[var(--accent-strong)] px-4 py-2 text-sm text-white hover:bg-[var(--accent-strong-hover)]";
const SECONDARY =
  "ml-3 mt-6 inline-block rounded-md border border-[var(--border-strong)] px-4 py-2 text-sm";

export function TenantInviteRedeemPage() {
  const { tenantSlug = "", token = "" } = useParams<{ tenantSlug: string; token: string }>();
  const t = useTranslations("invite");
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const hasHydrated = useAuthStore((state) => state.hasHydrated);
  const clearSession = useAuthStore((state) => state.clearSession);
  const [switching, setSwitching] = useState(false);
  // N13: nothing is redeemed until the signed-in person confirms the invite is for them.
  const [confirmed, setConfirmed] = useState(false);

  /** Sign out here and keep the invite, so the right person can sign in and accept it. */
  const signOutAndKeepInvite = async () => {
    setSwitching(true);
    await logoutSession().catch(() => undefined);
    broadcastSignOut("logout");
    clearSession();
    clearSessionActivity();
    rememberPendingInvite(tenantSlug, token);
    navigate(`/t/${tenantSlug}/login`);
  };

  if (!isAuthenticated) {
    if (!hasHydrated) {
      return (
        <main className="mx-auto max-w-lg px-4 py-16" data-testid="invite-redeem-page">
          <h1 className="text-2xl font-semibold">{t("title")}</h1>
          <p className="mt-4 text-[var(--text-muted)]">{t("loading")}</p>
        </main>
      );
    }

    // Sign-in and sign-up both come back here once the person has a session.
    rememberPendingInvite(tenantSlug, token);

    return (
      <main className="mx-auto max-w-lg px-4 py-16" data-testid="invite-redeem-page">
        <h1 className="text-2xl font-semibold">{t("title")}</h1>
        <p className="mt-4 text-[var(--text-muted)]">{t("signInOrRegister")}</p>
        <Link to={`/t/${tenantSlug}/login`} className={PRIMARY} data-testid="invite-login-link">
          {t("signIn")}
        </Link>
        <Link
          to={`/t/${tenantSlug}/register`}
          className={SECONDARY}
          data-testid="invite-register-link"
        >
          {t("createAccount")}
        </Link>
      </main>
    );
  }

  // AUTH-10: signed in to another tenant — say so rather than silently redirecting.
  if (user?.tenantSlug && user.tenantSlug !== tenantSlug) {
    const current = user.tenantSlug;

    return (
      <main className="mx-auto max-w-lg px-4 py-16" data-testid="invite-redeem-page">
        <h1 className="text-2xl font-semibold">{t("title")}</h1>
        <p className="mt-4 text-[var(--text-muted)]" data-testid="invite-wrong-tenant">
          {t("wrongTenant", { current, tenant: tenantSlug })}
        </p>
        <button
          type="button"
          className={PRIMARY}
          disabled={switching}
          onClick={() => void signOutAndKeepInvite()}
          data-testid="invite-switch-account"
        >
          {t("switchAccount")}
        </button>
        <Link
          to={`/t/${current}/dashboard`}
          className={SECONDARY}
          data-testid="invite-own-dashboard"
        >
          {t("goToMyDashboard")}
        </Link>
      </main>
    );
  }

  // N13: an invite remembered in this browser (or a link opened while someone else is signed in)
  // must not be redeemed silently for whoever happens to be signed in — they confirm first.
  if (!confirmed) {
    return (
      <main className="mx-auto max-w-lg px-4 py-16" data-testid="invite-redeem-page">
        <h1 className="text-2xl font-semibold">{t("title")}</h1>
        <p className="mt-4 text-[var(--text-muted)]" data-testid="invite-confirm">
          {t("confirmPrompt", { email: user?.email ?? "" })}
        </p>
        <button
          type="button"
          className={PRIMARY}
          disabled={switching}
          onClick={() => setConfirmed(true)}
          data-testid="invite-confirm-accept"
        >
          {t("confirmAccept")}
        </button>
        <button
          type="button"
          className={SECONDARY}
          disabled={switching}
          onClick={() => void signOutAndKeepInvite()}
          data-testid="invite-confirm-switch"
        >
          {t("confirmNotMe")}
        </button>
      </main>
    );
  }

  return <InviteRedeemPanel tenantSlug={tenantSlug} token={token} />;
}
